import React from 'react';
import moment from 'moment-timezone';

import Strings from '../../../Strings';
import { roundToDecimal } from "../../../utils/unitConverters";
import { graphContainer } from "../../Dashboards/Widgets/graphContainers";
import {
  OverallActivePatientsMetadata,
  OverallAllPatientsMetadata,
  OverallNewPatientsTrendMetadata,
  OverallNewEnrollmentsTrendMetadata,
  OverallActiveEnrollmentsTrendMetadata,
  OverallPatientsCountTrendMetadata,
  OverallDisenrollmentsTrendMetadata,
  OverallDisenrollmentReasonsTrendMetadata,
  OverallNotEnrolledTrendMetadata,
  OverallNotEnrolledReasonsTrendMetadata,
  OrgsStateMetadata,
  OrgsAdministrativeStateMetadata,
  OrgsBusinessStateMetadata,
  OrgsOperationalStateMetadata,
  NewOrganizationsByTypeMetadata,
  OrganizationsByPatientsMetadata,
  OrganizationsByEnrollmentsMetadata,
  OrganizationsByKitsOrderedMetadata,
  OrganizationsByDevicesOrderedMetadata,
  ENROLLMENT_TREND_STATUSES,
  EnrollmentEventTrendMetadata,
  EnrollmentSnapshotTrendMetadata,
  AvgTimeInProgramTrendMetadata,
  EligibleToEnrolledRatioMetadata,
  UniquePatientsTrendMetadata,
  OnboardedToRegisteredTrendMetadata,
  RegisteredToEnrolledTrendMetadata,
  EnrolledToActivatedTrendMetadata,
  KitsOrderedTotalTrendMetadata,
  TotalKitsOrderedTrendMetadata,
  KitsOrderedTrendMetadata,
  BulkKitsOrderedTrendMetadata,
  KitsShippedTotalTrendMetadata,
  TotalKitsShippedTrendMetadata,
  KitsShippedTrendMetadata,
  BulkKitsShippedTrendMetadata,
  DevicesShippedTotalTrendMetadata,
  TotalDevicesShippedTrendMetadata,
  KitsShippingQueueTrendMetadata,
  DevicesShippedTrendMetadata,
  BulkDevicesShippedTrendMetadata,
  AvgOrderToShippedTrendMetadata,
  BulkAvgOrderToShippedTrendMetadata,
  AvgShippedToActivatedTrendMetadata,
  KitUsageTrendMetadata,
  DeviceUsageTrendMetadata,
  AssignedKitsTrendMetadata,
  AssignedDevicesTrendMetadata,
  CapsLastTakeMetadata,
  DevicesLastActivityMetadata,
  ShipmentMethodSplitMetadata,
  DisenrollmentReasonsMetadata,
  NotEnrolledReasonsMetadata,
  NotEnrolledRecoveriesMetadata,
  numberWithTitleWidget,
} from './businessReportWidgetsMetadata';
import { getFiltersDefinition } from '../../../components/AdvancedFilters/FiltersDefinition';
import OrganizationsState from '../../../components/Graphs/OrganizationsState';
import GenericCategoryGraph, { chartTypeEnum } from '../../../components/Graphs/GenericCategoryGraph';
import { manipulateColor } from '../../../components/Graphs/CompositeGraph/ColorTools';

const buildPeriodLabel = (period, baseRange) => {
  if (!period?.start || !period?.end) {
    return '-';
  }

  const start = moment.parseZone(period.start);
  const end = moment.parseZone(period.end);
  if (!start.isValid() || !end.isValid()) {
    return '-';
  }

  if (baseRange === 'day') {
    return start.format('MMM D');
  }
  if (baseRange === 'month') {
    return start.format('MMM');
  }

  if (start.isSame(end, 'day')) {
    return start.format('MMM D');
  }
  if (start.isSame(end, 'month')) {
    return `${start.format('MMM D')} - ${end.format('D')}`;
  }
  return `${start.format('MMM D')} - ${end.format('MMM D')}`;
};

const buildReasonLabel = reason => {
  if (!reason) {
    return 'Unknown';
  }
  if (reason.includes(' ')) {
    return reason;
  }
  return reason
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, text => text.toUpperCase());
};

const buildOrganizationLabel = item => {
  const orgType = buildReasonLabel(item?.org_type);
  return orgType ? `${item?.name || 'Unknown'} (${orgType})` : (item?.name || 'Unknown');
};

const aggregateReasonCounts = (periods, key) => {
  const aggregated = periods.reduce((acc, period) => {
    (period?.[key] || []).forEach(entry => {
      const reason = buildReasonLabel(entry.reason);
      acc[reason] = (acc[reason] || 0) + (entry.count || 0);
    });
    return acc;
  }, {});

  return Object.entries(aggregated)
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
};

const buildShipmentProgramTrendData = (periods, breakdownKey, totalKey, baseRange) => {
  const allPrograms = Array.from(new Set(
    periods.flatMap(period => (period?.events?.[breakdownKey] || []).map(item => item.program)).filter(Boolean)
  )).sort();

  const graphData = periods.map(period => {
    const row = {
      period: buildPeriodLabel(period, baseRange),
      total: period?.events?.[totalKey] || 0,
    };
    (period?.events?.[breakdownKey] || []).forEach(item => {
      row[item.program] = item.count || 0;
    });
    return row;
  });

  return { allPrograms, graphData };
};

const formatTransitionTimeTooltip = (periodLabel, seriesIndex, value, dataSeries, dataByPeriod, countKey) => {
  const count = dataByPeriod[periodLabel]?.[countKey] || 0;
  return {
    text: `${dataSeries[seriesIndex].name}: ${roundToDecimal(value, 2)} d, Transitions: ${count}`,
    lines: [
      `${periodLabel}:`,
      `${dataSeries[seriesIndex].name}: ${roundToDecimal(value, 2)} d`,
      `Transitions: ${count}`,
    ],
  };
};

const formatShipmentAverageTooltip = (periodLabel, seriesIndex, value, dataSeries, dataByPeriod, countKey) => {
  const count = dataByPeriod[periodLabel]?.[countKey] || 0;
  return {
    text: `${dataSeries[seriesIndex].name}: ${roundToDecimal(value, 2)} d, Shipments: ${count}`,
    lines: [
      `${periodLabel}:`,
      `${dataSeries[seriesIndex].name}: ${roundToDecimal(value, 2)} d`,
      `Shipments: ${count}`,
    ],
  };
};

export default function initializeDashboard(
  activeTab,
  enrollmentsSummaryObj,
  shipmentSummaryObj,
  deviceUtilizationSummaryObj,
  organizationsSummaryObj,
  orgsObj,
  setWidgets,
  configuration,
  colorPalette,
  dashboardSettingsFeature,
) {
  const enrollmentsSummary = enrollmentsSummaryObj?.data || {};
  const shipmentSummary = shipmentSummaryObj?.data || {};
  const deviceUtilizationSummary = deviceUtilizationSummaryObj?.data || {};
  const organizationsSummary = organizationsSummaryObj?.data || {};
  const orgs = orgsObj?.data || [];
  const loadingEnrollmentsSummary = !enrollmentsSummaryObj?.loaded;
  const loadingShipmentSummary = !shipmentSummaryObj?.loaded;
  const loadingDeviceUtilizationSummary = !deviceUtilizationSummaryObj?.loaded;
  const loadingOrganizationsSummary = !organizationsSummaryObj?.loaded;
  const loadingOrgs = !orgsObj?.loaded;
  const enrollmentsBaseRange = enrollmentsSummary?.meta?.base_range;
  const shipmentBaseRange = shipmentSummary?.meta?.base_range;
  const deviceUtilizationBaseRange = deviceUtilizationSummary?.meta?.base_range;
  const organizationsBaseRange = organizationsSummary?.meta?.base_range;

  const getOrgsStatesData = () => {
    const ret = [
      {
        type: 'Administrative',
      },
      {
        type: 'Business',
      },
      {
        type: 'Operational',
      },
    ];
    getFiltersDefinition()
      .org_administrative_state.values.filter(f => f.value !== 'all')
      .forEach(f => { ret[0][f.text] = orgs.filter(o => o.state.administrative_state === f.value).length; });
    getFiltersDefinition()
      .org_business_state.values.filter(f => f.value !== 'all')
      .forEach(f => { ret[1][f.text] = orgs.filter(o => o.state.business_state === f.value).length; });
    getFiltersDefinition()
      .org_operational_state.values.filter(f => f.value !== 'all')
      .forEach(f => { ret[2][f.text] = orgs.filter(o => o.state.operational_state === f.value).length; });
    return ret;
  };
  const organizationSummaryPeriods = organizationsSummary?.periods || [];
  const newOrganizationsTrendData = organizationSummaryPeriods.map(period => ({
    period: buildPeriodLabel(period, organizationsBaseRange),
    client: period?.new_organizations?.client || 0,
    location: period?.new_organizations?.location || 0,
    partner: period?.new_organizations?.partner || 0,
  }));
  const organizationsByPatientsData = (organizationsSummary?.rankings?.by_patients || []).map(item => ({
    organization: buildOrganizationLabel(item),
    count: item.count || 0,
  }));
  const organizationsByEnrollmentsData = (organizationsSummary?.rankings?.by_enrollments || []).map(item => ({
    organization: buildOrganizationLabel(item),
    count: item.count || 0,
  }));
  const organizationsByKitsOrderedData = (organizationsSummary?.rankings?.by_kits_ordered || []).map((item, index, items) => {
    const baseColor = colorPalette?.interpolate?.((index + 1) / (items.length + 1)) || '#77A0D9';
    return {
      organization: buildOrganizationLabel(item),
      count: item.count || 0,
      direct: item.direct_count || 0,
      bulk: item.bulk_count || 0,
      cellColors: {
        direct: baseColor,
        bulk: manipulateColor(0.2, baseColor, '#ffffff', true) || baseColor,
      },
    };
  });
  const organizationsByDevicesOrderedData = (organizationsSummary?.rankings?.by_devices_ordered || []).map(item => ({
    organization: buildOrganizationLabel(item),
    count: item.count || 0,
  }));

  const widgetOrgsState = {
    ...OrgsStateMetadata,
    noPadding: true,
    render: graphContainer(
      <OrganizationsState
        graphData={getOrgsStatesData()}
        colorPalette={colorPalette}
        graphType={
          configuration?.[OrgsStateMetadata.id]?.chartType?.value ||
          OrgsStateMetadata.configuration.chartType.defaultValue
        }
      />,
      OrgsStateMetadata.name,
    ),
    loading: loadingOrgs,
  };

  const widgetOrgsAdmState = {
    ...OrgsAdministrativeStateMetadata,
    noPadding: true,
    render: graphContainer(
      <OrganizationsState
        graphData={getFiltersDefinition()
          .org_administrative_state.values.filter(f => f.value !== 'all')
          .map(f => ({
            type: f.text,
            countOfOrgs: orgs.filter(o => o.state.administrative_state === f.value).length,
          }))}
        dataSelector="countOfOrgs"
        title={OrgsAdministrativeStateMetadata.name}
        colorPalette={colorPalette}
        graphType={
          configuration?.[OrgsAdministrativeStateMetadata.id]?.chartType?.value ||
          OrgsAdministrativeStateMetadata.configuration.chartType.defaultValue
        }
      />,
      OrgsAdministrativeStateMetadata.name,
    ),
    loading: loadingOrgs,
  };

  const widgetOrgBusinessAdmState = {
    ...OrgsBusinessStateMetadata,
    noPadding: true,
    render: graphContainer(
      <OrganizationsState
        graphData={getFiltersDefinition()
          .org_business_state.values.filter(f => f.value !== 'all')
          .map(f => ({ type: f.text, countOfOrgs: orgs.filter(o => o.state.business_state === f.value).length }))}
        dataSelector="countOfOrgs"
        title={OrgsBusinessStateMetadata.name}
        colorPalette={colorPalette}
        graphType={
          configuration?.[OrgsBusinessStateMetadata.id]?.chartType?.value ||
          OrgsBusinessStateMetadata.configuration.chartType.defaultValue
        }
      />,
      OrgsBusinessStateMetadata.name,
    ),
    loading: loadingOrgs,
  };

  const widgetOrgsOpState = {
    ...OrgsOperationalStateMetadata,
    noPadding: true,
    render: graphContainer(
      <OrganizationsState
        graphData={getFiltersDefinition()
          .org_operational_state.values.filter(f => f.value !== 'all')
          .map(f => ({ type: f.text, countOfOrgs: orgs.filter(o => o.state.operational_state === f.value).length }))}
        dataSelector="countOfOrgs"
        title={OrgsOperationalStateMetadata.name}
        colorPalette={colorPalette}
        graphType={
          configuration?.[OrgsOperationalStateMetadata.id]?.chartType?.value ||
          OrgsOperationalStateMetadata.configuration.chartType.defaultValue
        }
      />,
      OrgsOperationalStateMetadata.name,
    ),
    loading: loadingOrgs,
  };

  const widgetNewOrganizationsByType = {
    ...NewOrganizationsByTypeMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={newOrganizationsTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={NewOrganizationsByTypeMetadata.name}
        dataSeries={[
          { name: 'Client', color: colorPalette?.interpolate(0.2), selector: item => item.client },
          { name: 'Location', color: colorPalette?.interpolate(0.5), selector: item => item.location },
          { name: 'Partner', color: colorPalette?.interpolate(0.8), selector: item => item.partner },
        ]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[NewOrganizationsByTypeMetadata.id]?.chartType?.value || chartTypeEnum.stackedBar}
        colorPalette={colorPalette}
        hasLegend
      />,
      NewOrganizationsByTypeMetadata.name,
    ),
    loading: loadingOrganizationsSummary,
  };

  const renderOrganizationRankingWidget = (metadata, graphData, colorIndex) => ({
    ...metadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={graphData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={metadata.name}
        dataSeries={[{ name: 'Count', color: colorPalette?.interpolate(colorIndex), selector: item => item.count }]}
        categorySelector={item => item.organization}
        hasReferenceAreas
        graphType={configuration?.[metadata.id]?.chartType?.value || chartTypeEnum.horizontalBar}
        colorPalette={colorPalette}
        yAxisWidth={220}
      />,
      metadata.name,
    ),
    loading: loadingOrganizationsSummary,
  });

  const widgetOrganizationsByKitsOrdered = {
    ...OrganizationsByKitsOrderedMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={organizationsByKitsOrderedData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={OrganizationsByKitsOrderedMetadata.name}
        dataSeries={[
          {
            name: 'Direct',
            color: colorPalette?.interpolate(0.5),
            selector: item => item.direct || 0,
            colorSelector: item => item.cellColors?.direct,
          },
          {
            name: 'Bulk',
            color: colorPalette?.interpolate(0.8),
            selector: item => item.bulk || 0,
            colorSelector: item => item.cellColors?.bulk,
          },
        ]}
        categorySelector={item => item.organization}
        hasReferenceAreas
        graphType={
          configuration?.[OrganizationsByKitsOrderedMetadata.id]?.chartType?.value ||
          OrganizationsByKitsOrderedMetadata.configuration.chartType.defaultValue
        }
        colorPalette={colorPalette}
        yAxisWidth={220}
      />,
      OrganizationsByKitsOrderedMetadata.name,
    ),
    loading: loadingOrganizationsSummary,
  };

  const widgetOrganizationsByPatients = renderOrganizationRankingWidget(
    OrganizationsByPatientsMetadata,
    organizationsByPatientsData,
    0.25,
  );
  const widgetOrganizationsByEnrollments = renderOrganizationRankingWidget(
    OrganizationsByEnrollmentsMetadata,
    organizationsByEnrollmentsData,
    0.45,
  );
  const widgetOrganizationsByDevicesOrdered = renderOrganizationRankingWidget(
    OrganizationsByDevicesOrderedMetadata,
    organizationsByDevicesOrderedData,
    0.85,
  );


  const summaryPeriods = enrollmentsSummary?.periods || [];
  const eventsTrendData = summaryPeriods.map(period => ({
    period: buildPeriodLabel(period, enrollmentsBaseRange),
    newPatients: period?.new_patients || 0,
    onboarded: period?.events?.onboarded || 0,
    registered: period?.events?.registered || 0,
    enrolled: period?.events?.enrolled || 0,
    activated: period?.events?.activated || 0,
    notEnrolled: period?.events?.not_enrolled || 0,
    disenrolled: period?.events?.disenrolled || 0,
  }));
  const snapshotTrendData = summaryPeriods.map(period => ({
    period: buildPeriodLabel(period, enrollmentsBaseRange),
    onboarded: period?.snapshot?.onboarded || 0,
    registered: period?.snapshot?.registered || 0,
    enrolled: period?.snapshot?.enrolled || 0,
    activated: period?.snapshot?.activated || 0,
    disenrolled: period?.snapshot?.disenrolled || 0,
    notEnrolled: period?.snapshot?.not_enrolled || 0,
  }));
  const transitionTimesTrendData = summaryPeriods.map(period => ({
    period: buildPeriodLabel(period, enrollmentsBaseRange),
    onboardedToRegistered:
      period?.transition_times?.onboarded_to_registered_avg_hours == null
        ? null
        : roundToDecimal(period.transition_times.onboarded_to_registered_avg_hours / 24, 2),
    onboardedToRegisteredCount: period?.transition_counts?.onboarded_to_registered || 0,
    registeredToEnrolled:
      period?.transition_times?.registered_to_enrolled_avg_hours == null
        ? null
        : roundToDecimal(period.transition_times.registered_to_enrolled_avg_hours / 24, 2),
    registeredToEnrolledCount: period?.transition_counts?.registered_to_enrolled || 0,
    enrolledToActivated:
      period?.transition_times?.enrolled_to_activated_avg_hours == null
        ? null
        : roundToDecimal(period.transition_times.enrolled_to_activated_avg_hours / 24, 2),
    enrolledToActivatedCount: period?.transition_counts?.enrolled_to_activated || 0,
  }));
  const uniquePatientsTrendData = summaryPeriods.map(period => ({
    period: buildPeriodLabel(period, enrollmentsBaseRange),
    uniquePatients: period?.unique_patients_at_end || 0,
  }));
  const overallActivePatientsTrendData = summaryPeriods.map(period => ({
    period: buildPeriodLabel(period, enrollmentsBaseRange),
    activePatients: period?.activated_patients_at_end || 0,
  }));
  const overallPatientsCountTrendData = summaryPeriods.map(period => ({
    period: buildPeriodLabel(period, enrollmentsBaseRange),
    patientsCount: period?.all_patients_at_end || 0,
  }));
  const avgTimeInProgramTrendData = summaryPeriods.map(period => ({
    period: buildPeriodLabel(period, enrollmentsBaseRange),
    avgTimeInProgramDays:
      period?.average_time_in_program_days == null ? null : roundToDecimal(period.average_time_in_program_days, 2),
  }));
  const eligibleToEnrolledRatioTrendData = summaryPeriods.map(period => ({
    period: buildPeriodLabel(period, enrollmentsBaseRange),
    eligibleToEnrolledRatio:
      period?.eligible_to_enrolled_ratio == null ? null : roundToDecimal(period.eligible_to_enrolled_ratio * 100, 2),
  }));
  const disenrollmentReasonsData = aggregateReasonCounts(summaryPeriods, 'disenrollment_reasons');
  const notEnrolledReasonsData = aggregateReasonCounts(summaryPeriods, 'not_enrolled_reasons');
  const notEnrolledRecoveriesData = summaryPeriods.reduce(
    (acc, period) => {
      acc[0].count += period?.transition_counts?.not_enrolled_to_onboarded || 0;
      acc[1].count += period?.transition_counts?.not_enrolled_to_registered || 0;
      return acc;
    },
    [
      { destination: 'To Onboarded', count: 0 },
      { destination: 'To Registered', count: 0 },
    ],
  );
  const disenrollmentReasonTrendData = (() => {
    const allReasons = Array.from(
      new Set(
        summaryPeriods.flatMap(period => (period?.disenrollment_reasons || []).map(entry => buildReasonLabel(entry.reason))),
      ),
    ).sort();

    return {
      allReasons,
      graphData: summaryPeriods.map(period => {
        const row = {
          period: buildPeriodLabel(period, enrollmentsBaseRange),
          total: period?.events?.disenrolled || 0,
        };
        (period?.disenrollment_reasons || []).forEach(entry => {
          row[buildReasonLabel(entry.reason)] = entry.count || 0;
        });
        return row;
      }),
    };
  })();
  const notEnrolledReasonTrendData = (() => {
    const allReasons = Array.from(
      new Set(
        summaryPeriods.flatMap(period => (period?.not_enrolled_reasons || []).map(entry => buildReasonLabel(entry.reason))),
      ),
    ).sort();

    return {
      allReasons,
      graphData: summaryPeriods.map(period => {
        const row = {
          period: buildPeriodLabel(period, enrollmentsBaseRange),
          total: period?.events?.not_enrolled || 0,
        };
        (period?.not_enrolled_reasons || []).forEach(entry => {
          row[buildReasonLabel(entry.reason)] = entry.count || 0;
        });
        return row;
      }),
    };
  })();

  const shipmentPeriods = shipmentSummary?.periods || [];
  const latestShipmentPeriod = shipmentPeriods[shipmentPeriods.length - 1] || {};
  const { allPrograms: orderedPrograms, graphData: kitsOrderedTrendData } = buildShipmentProgramTrendData(
    shipmentPeriods,
    'kits_ordered_by_program',
    'direct_kits_ordered',
    shipmentBaseRange,
  );
  const { allPrograms: shippedPrograms, graphData: kitsShippedTrendData } = buildShipmentProgramTrendData(
    shipmentPeriods,
    'kits_shipped_by_program',
    'direct_kits_shipped',
    shipmentBaseRange,
  );
  const kitsOrderedTotalTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    kitsOrdered: period?.events?.kits_ordered || 0,
  }));
  const totalKitsOrderedTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    directKitsOrdered: period?.cumulative?.direct_kits_ordered || 0,
    bulkKitsOrdered: period?.cumulative?.bulk_kits_ordered || 0,
  }));
  const bulkKitsOrderedTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    bulkKitsOrdered: period?.events?.bulk_kits_ordered || 0,
  }));
  const kitsShippedTotalTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    kitsShipped: period?.events?.kits_shipped || 0,
  }));
  const totalKitsShippedTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    directKitsShipped: period?.cumulative?.direct_kits_shipped || 0,
    bulkKitsShipped: period?.cumulative?.bulk_kits_shipped || 0,
  }));
  const bulkKitsShippedTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    bulkKitsShipped: period?.events?.bulk_kits_shipped || 0,
  }));
  const devicesShippedTotalTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    devicesShipped: period?.events?.devices_shipped || 0,
  }));
  const totalDevicesShippedTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    directDevicesShipped: period?.cumulative?.direct_devices_shipped || 0,
    bulkDevicesShipped: period?.cumulative?.bulk_devices_shipped || 0,
  }));
  const kitsShippingQueueTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    kitsShippingQueue: (period?.cumulative?.kits_ordered || 0) - (period?.cumulative?.kits_shipped || 0),
  }));
  const devicesShippedTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    devicesShipped: period?.events?.direct_devices_shipped || 0,
  }));
  const bulkDevicesShippedTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    bulkDevicesShipped: period?.events?.bulk_devices_shipped || 0,
  }));
  const avgOrderToShippedTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    orderToShippedCount: period?.fulfillment_counts?.direct_order_to_shipped || 0,
    avgOrderToShippedDays:
      period?.fulfillment_times?.direct_order_to_shipped_avg_hours == null
        ? null
        : roundToDecimal(period.fulfillment_times.direct_order_to_shipped_avg_hours / 24, 2),
  }));
  const bulkAvgOrderToShippedTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    orderToShippedCount: period?.fulfillment_counts?.bulk_order_to_shipped || 0,
    avgOrderToShippedDays:
      period?.fulfillment_times?.bulk_order_to_shipped_avg_hours == null
        ? null
        : roundToDecimal(period.fulfillment_times.bulk_order_to_shipped_avg_hours / 24, 2),
  }));
  const avgShippedToActivatedTrendData = shipmentPeriods.map(period => ({
    period: buildPeriodLabel(period, shipmentBaseRange),
    shippedToActivatedCount: period?.fulfillment_counts?.shipped_to_activated || 0,
    avgShippedToActivatedDays:
      period?.fulfillment_times?.shipped_to_activated_avg_hours == null
        ? null
        : roundToDecimal(period.fulfillment_times.shipped_to_activated_avg_hours / 24, 2),
  }));
  const deviceUtilizationPeriods = deviceUtilizationSummary?.periods || [];
  const kitUsageTrendData = deviceUtilizationPeriods.map(period => ({
    period: buildPeriodLabel(period, deviceUtilizationBaseRange),
    averageKitUsagePercent:
      period?.utilization?.average_kit_usage_percent == null
        ? null
        : roundToDecimal(period.utilization.average_kit_usage_percent, 2),
  }));
  const deviceUsageTrendData = deviceUtilizationPeriods.map(period => ({
    period: buildPeriodLabel(period, deviceUtilizationBaseRange),
    averageDeviceUsagePercent:
      period?.utilization?.average_device_usage_percent == null
        ? null
        : roundToDecimal(period.utilization.average_device_usage_percent, 2),
  }));
  const assignedKitsTrendData = deviceUtilizationPeriods.map(period => ({
    period: buildPeriodLabel(period, deviceUtilizationBaseRange),
    assignedKits: period?.utilization?.assigned_kits || 0,
  }));
  const assignedDevicesTrendData = deviceUtilizationPeriods.map(period => ({
    period: buildPeriodLabel(period, deviceUtilizationBaseRange),
    assignedDevices: period?.utilization?.assigned_devices || 0,
  }));
  const shipmentMethodSplitData = (latestShipmentPeriod?.shipment_method_split || []).map(item => ({
    shipmentMethod: buildReasonLabel(item.shipment_method),
    count: item.count || 0,
  }));

  const widgetUniquePatientsTrend = {
    ...UniquePatientsTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={uniquePatientsTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={UniquePatientsTrendMetadata.name}
        dataSeries={[{ name: 'Unique Patients', color: colorPalette?.interpolate(0.2), selector: item => item.uniquePatients }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[UniquePatientsTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      UniquePatientsTrendMetadata.name,
    ),
    loading: loadingEnrollmentsSummary,
  };
  const latestSummaryPeriod = summaryPeriods[summaryPeriods.length - 1] || {};
  const widgetOverallActivePatients = numberWithTitleWidget(
    OverallActivePatientsMetadata,
    latestSummaryPeriod?.activated_patients_at_end,
    dashboardSettingsFeature,
    undefined,
    '',
    OverallActivePatientsMetadata.description,
  );
  const widgetOverallAllPatients = numberWithTitleWidget(
    OverallAllPatientsMetadata,
    latestSummaryPeriod?.all_patients_at_end,
    dashboardSettingsFeature,
    undefined,
    '',
    OverallAllPatientsMetadata.description,
  );
  widgetOverallActivePatients.loading = loadingEnrollmentsSummary;
  widgetOverallAllPatients.loading = loadingEnrollmentsSummary;
  const widgetOverallNewPatientsTrend = {
    ...OverallNewPatientsTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={eventsTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={OverallNewPatientsTrendMetadata.name}
        dataSeries={[{ name: 'New Patients', color: colorPalette?.interpolate(0.2), selector: item => item.newPatients }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[OverallNewPatientsTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      OverallNewPatientsTrendMetadata.name,
      OverallNewPatientsTrendMetadata.description,
    ),
    loading: loadingEnrollmentsSummary,
  };
  const widgetOverallNewEnrollmentsTrend = {
    ...OverallNewEnrollmentsTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={eventsTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={OverallNewEnrollmentsTrendMetadata.name}
        dataSeries={[{ name: 'New Enrollments', color: colorPalette?.interpolate(0.5), selector: item => item.enrolled }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[OverallNewEnrollmentsTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      OverallNewEnrollmentsTrendMetadata.name,
      OverallNewEnrollmentsTrendMetadata.description,
    ),
    loading: loadingEnrollmentsSummary,
  };
  const widgetOverallActiveEnrollmentsTrend = {
    ...OverallActiveEnrollmentsTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={overallActivePatientsTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={OverallActiveEnrollmentsTrendMetadata.name}
        dataSeries={[{ name: 'Active Patients', color: colorPalette?.interpolate(0.65), selector: item => item.activePatients }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[OverallActiveEnrollmentsTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      OverallActiveEnrollmentsTrendMetadata.name,
      OverallActiveEnrollmentsTrendMetadata.description,
    ),
    loading: loadingEnrollmentsSummary,
  };
  const widgetOverallPatientsCountTrend = {
    ...OverallPatientsCountTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={overallPatientsCountTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={OverallPatientsCountTrendMetadata.name}
        dataSeries={[{ name: 'Patients Count', color: colorPalette?.interpolate(0.35), selector: item => item.patientsCount }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[OverallPatientsCountTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      OverallPatientsCountTrendMetadata.name,
      OverallPatientsCountTrendMetadata.description,
    ),
    loading: loadingEnrollmentsSummary,
  };
  const widgetOverallDisenrollmentsTrend = {
    ...OverallDisenrollmentsTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={snapshotTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={OverallDisenrollmentsTrendMetadata.name}
        dataSeries={[{ name: 'Disenrollments', color: colorPalette?.interpolate(0.8), selector: item => item.disenrolled }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[OverallDisenrollmentsTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      OverallDisenrollmentsTrendMetadata.name,
      OverallDisenrollmentsTrendMetadata.description,
    ),
    loading: loadingEnrollmentsSummary,
  };
  const widgetOverallDisenrollmentReasonsTrend = {
    ...OverallDisenrollmentReasonsTrendMetadata,
    noPadding: true,
    render: (() => {
      const dataSeries = disenrollmentReasonTrendData.allReasons.length > 0
        ? disenrollmentReasonTrendData.allReasons.map((reason, index) => ({
            name: reason,
            color: colorPalette?.interpolate((index + 1) / (disenrollmentReasonTrendData.allReasons.length + 1)),
            selector: item => item[reason] || 0,
          }))
        : [{ name: 'Disenrollments', color: colorPalette?.interpolate(0.8), selector: item => item.total || 0 }];
      return graphContainer(
        <GenericCategoryGraph
          graphData={disenrollmentReasonTrendData.graphData}
          hasBorder
          forceCondensed
          xAxisTopPosition={false}
          yAxisUnit=""
          tooltipTitle={OverallDisenrollmentReasonsTrendMetadata.name}
          dataSeries={dataSeries}
          categorySelector={item => item.period}
          hasReferenceAreas
          graphType={configuration?.[OverallDisenrollmentReasonsTrendMetadata.id]?.stackedChartType?.value || chartTypeEnum.stackedBar}
          colorPalette={colorPalette}
        />,
        OverallDisenrollmentReasonsTrendMetadata.name,
        OverallDisenrollmentReasonsTrendMetadata.description,
      );
    })(),
    loading: loadingEnrollmentsSummary,
  };
  const widgetOverallNotEnrolledTrend = {
    ...OverallNotEnrolledTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={snapshotTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={OverallNotEnrolledTrendMetadata.name}
        dataSeries={[{ name: 'Not Enrolled', color: colorPalette?.interpolate(0.92), selector: item => item.notEnrolled }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[OverallNotEnrolledTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      OverallNotEnrolledTrendMetadata.name,
      OverallNotEnrolledTrendMetadata.description,
    ),
    loading: loadingEnrollmentsSummary,
  };
  const widgetOverallNotEnrolledReasonsTrend = {
    ...OverallNotEnrolledReasonsTrendMetadata,
    noPadding: true,
    render: (() => {
      const dataSeries = notEnrolledReasonTrendData.allReasons.length > 0
        ? notEnrolledReasonTrendData.allReasons.map((reason, index) => ({
            name: reason,
            color: colorPalette?.interpolate((index + 1) / (notEnrolledReasonTrendData.allReasons.length + 1)),
            selector: item => item[reason] || 0,
          }))
        : [{ name: 'Not Enrolled', color: colorPalette?.interpolate(0.92), selector: item => item.total || 0 }];
      return graphContainer(
        <GenericCategoryGraph
          graphData={notEnrolledReasonTrendData.graphData}
          hasBorder
          forceCondensed
          xAxisTopPosition={false}
          yAxisUnit=""
          tooltipTitle={OverallNotEnrolledReasonsTrendMetadata.name}
          dataSeries={dataSeries}
          categorySelector={item => item.period}
          hasReferenceAreas
          graphType={configuration?.[OverallNotEnrolledReasonsTrendMetadata.id]?.stackedChartType?.value || chartTypeEnum.stackedBar}
          colorPalette={colorPalette}
        />,
        OverallNotEnrolledReasonsTrendMetadata.name,
        OverallNotEnrolledReasonsTrendMetadata.description,
      );
    })(),
    loading: loadingEnrollmentsSummary,
  };
  const widgetAvgTimeInProgramTrend = {
    ...AvgTimeInProgramTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={avgTimeInProgramTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=" d"
        tooltipTitle={AvgTimeInProgramTrendMetadata.name}
        dataSeries={[{ name: 'Avg Time in Program', color: colorPalette?.interpolate(0.45), selector: item => item.avgTimeInProgramDays }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[AvgTimeInProgramTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      AvgTimeInProgramTrendMetadata.name,
    ),
    loading: loadingEnrollmentsSummary,
  };
  const widgetEligibleToEnrolledRatioTrend = {
    ...EligibleToEnrolledRatioMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={eligibleToEnrolledRatioTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit="%"
        tooltipTitle={EligibleToEnrolledRatioMetadata.name}
        dataSeries={[{ name: 'Enrolled / Eligible', color: colorPalette?.interpolate(0.7), selector: item => item.eligibleToEnrolledRatio }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[EligibleToEnrolledRatioMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      EligibleToEnrolledRatioMetadata.name,
    ),
    loading: loadingEnrollmentsSummary,
  };
  const transitionTimesByPeriod = transitionTimesTrendData.reduce((acc, item) => {
    acc[item.period] = item;
    return acc;
  }, {});

  const buildTransitionTimeWidget = (metadata, selectorKey, countKey, color) => {
    const transitionTimeSeries = [{ name: metadata.name, color, selector: item => item[selectorKey] }];
    return {
      ...metadata,
      noPadding: true,
      render: graphContainer(
        <GenericCategoryGraph
          graphData={transitionTimesTrendData}
          hasBorder
          forceCondensed
          xAxisTopPosition={false}
          yAxisUnit=" d"
          tooltipTitle={metadata.name}
          dataSeries={transitionTimeSeries}
          labelFormatter={(periodLabel, seriesIndex, value) =>
            formatTransitionTimeTooltip(periodLabel, seriesIndex, value, transitionTimeSeries, transitionTimesByPeriod, countKey)}
          categorySelector={item => item.period}
          hasReferenceAreas
          graphType={configuration?.[metadata.id]?.chartType?.value || chartTypeEnum.line}
          colorPalette={colorPalette}
        />,
        metadata.name,
      ),
      loading: loadingEnrollmentsSummary,
    };
  };

  const widgetOnboardedToRegisteredTrend = buildTransitionTimeWidget(
    OnboardedToRegisteredTrendMetadata,
    'onboardedToRegistered',
    'onboardedToRegisteredCount',
    colorPalette?.interpolate(0.15),
  );
  const widgetRegisteredToEnrolledTrend = buildTransitionTimeWidget(
    RegisteredToEnrolledTrendMetadata,
    'registeredToEnrolled',
    'registeredToEnrolledCount',
    colorPalette?.interpolate(0.45),
  );
  const widgetEnrolledToActivatedTrend = buildTransitionTimeWidget(
    EnrolledToActivatedTrendMetadata,
    'enrolledToActivated',
    'enrolledToActivatedCount',
    colorPalette?.interpolate(0.75),
  );

  const statusColorMap = {
    onboarded: colorPalette?.interpolate(0.05),
    registered: colorPalette?.interpolate(0.3),
    enrolled: colorPalette?.interpolate(0.5),
    activated: colorPalette?.interpolate(0.7),
    disenrolled: colorPalette?.interpolate(0.85),
    notEnrolled: colorPalette?.interpolate(0.95),
  };

  const statusTrendWidgets = ENROLLMENT_TREND_STATUSES.flatMap((status, index) => {
    const eventMetadata = EnrollmentEventTrendMetadata[index];
    const snapshotMetadata = EnrollmentSnapshotTrendMetadata[index];
    const color = statusColorMap[status.key];

    const eventWidget = {
      ...eventMetadata,
      noPadding: true,
      render: graphContainer(
        <GenericCategoryGraph
          graphData={eventsTrendData}
          hasBorder
          forceCondensed
          xAxisTopPosition={false}
          yAxisUnit=""
          tooltipTitle={eventMetadata.name}
          dataSeries={[{ name: status.label, color, selector: item => item[status.key] }]}
          categorySelector={item => item.period}
          hasReferenceAreas
          graphType={configuration?.[eventMetadata.id]?.chartType?.value || chartTypeEnum.bar}
          colorPalette={colorPalette}
        />,
        eventMetadata.name,
      ),
      loading: loadingEnrollmentsSummary,
    };

    const snapshotWidget = {
      ...snapshotMetadata,
      noPadding: true,
      render: graphContainer(
        <GenericCategoryGraph
          graphData={snapshotTrendData}
          hasBorder
          forceCondensed
          xAxisTopPosition={false}
          yAxisUnit=""
          tooltipTitle={snapshotMetadata.name}
          dataSeries={[{ name: status.label, color, selector: item => item[status.key] }]}
          categorySelector={item => item.period}
          hasReferenceAreas
          graphType={configuration?.[snapshotMetadata.id]?.chartType?.value || chartTypeEnum.line}
          colorPalette={colorPalette}
        />,
        snapshotMetadata.name,
      ),
      loading: loadingEnrollmentsSummary,
    };

    return [eventWidget, snapshotWidget];
  });

  const widgetDisenrollmentReasons = {
    ...DisenrollmentReasonsMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={disenrollmentReasonsData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={DisenrollmentReasonsMetadata.name}
        dataSeries={[{ name: 'Count', color: colorPalette?.interpolate(0.8), selector: item => item.count }]}
        categorySelector={item => item.reason}
        hasReferenceAreas
        graphType={configuration?.[DisenrollmentReasonsMetadata.id]?.chartType?.value || chartTypeEnum.horizontalBar}
        colorPalette={colorPalette}
        yAxisWidth={150}
      />,
      DisenrollmentReasonsMetadata.name,
    ),
    loading: loadingEnrollmentsSummary,
  };

  const widgetNotEnrolledReasons = {
    ...NotEnrolledReasonsMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={notEnrolledReasonsData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={NotEnrolledReasonsMetadata.name}
        dataSeries={[{ name: 'Count', color: colorPalette?.interpolate(0.55), selector: item => item.count }]}
        categorySelector={item => item.reason}
        hasReferenceAreas
        graphType={configuration?.[NotEnrolledReasonsMetadata.id]?.chartType?.value || chartTypeEnum.horizontalBar}
        colorPalette={colorPalette}
        yAxisWidth={150}
      />,
      NotEnrolledReasonsMetadata.name,
    ),
    loading: loadingEnrollmentsSummary,
  };

  const widgetNotEnrolledRecoveries = {
    ...NotEnrolledRecoveriesMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={notEnrolledRecoveriesData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={NotEnrolledRecoveriesMetadata.name}
        dataSeries={[{ name: 'Count', color: colorPalette?.interpolate(0.3), selector: item => item.count }]}
        categorySelector={item => item.destination}
        hasReferenceAreas
        graphType={configuration?.[NotEnrolledRecoveriesMetadata.id]?.chartType?.value || chartTypeEnum.horizontalBar}
        colorPalette={colorPalette}
        yAxisWidth={120}
      />,
      NotEnrolledRecoveriesMetadata.name,
    ),
    loading: loadingEnrollmentsSummary,
  };

  const widgetKitsOrderedTotalTrend = {
    ...KitsOrderedTotalTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={kitsOrderedTotalTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={KitsOrderedTotalTrendMetadata.name}
        dataSeries={[{ name: 'Kits Ordered', color: colorPalette?.interpolate(0.18), selector: item => item.kitsOrdered }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[KitsOrderedTotalTrendMetadata.id]?.chartType?.value || chartTypeEnum.bar}
        colorPalette={colorPalette}
      />,
      KitsOrderedTotalTrendMetadata.name,
      KitsOrderedTotalTrendMetadata.description,
    ),
    loading: loadingShipmentSummary,
  };

  const widgetTotalKitsOrderedTrend = {
    ...TotalKitsOrderedTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={totalKitsOrderedTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={TotalKitsOrderedTrendMetadata.name}
        dataSeries={[
          { name: 'Direct', color: colorPalette?.interpolate(0.2), selector: item => item.directKitsOrdered },
          { name: 'Bulk', color: colorPalette?.interpolate(0.45), selector: item => item.bulkKitsOrdered },
        ]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[TotalKitsOrderedTrendMetadata.id]?.stackedChartType?.value || TotalKitsOrderedTrendMetadata.configuration.stackedChartType.defaultValue}
        colorPalette={colorPalette}
      />,
      TotalKitsOrderedTrendMetadata.name,
      TotalKitsOrderedTrendMetadata.description,
    ),
    loading: loadingShipmentSummary,
  };

  const widgetKitsOrderedTrend = {
    ...KitsOrderedTrendMetadata,
    noPadding: true,
    render: (() => {
      const dataSeries = orderedPrograms.length > 0
        ? orderedPrograms.map((program, index) => ({
            name: buildReasonLabel(program),
            color: colorPalette?.interpolate((index + 1) / (orderedPrograms.length + 1)),
            selector: item => item[program] || 0,
          }))
        : [{ name: 'Kits Ordered', color: colorPalette?.interpolate(0.2), selector: item => item.total }];
      return graphContainer(
        <GenericCategoryGraph
          graphData={kitsOrderedTrendData}
          hasBorder
          forceCondensed
          xAxisTopPosition={false}
          yAxisUnit=""
          tooltipTitle={KitsOrderedTrendMetadata.name}
          dataSeries={dataSeries}
          categorySelector={item => item.period}
          hasReferenceAreas
          graphType={configuration?.[KitsOrderedTrendMetadata.id]?.stackedChartType?.value || chartTypeEnum.stackedBar}
          colorPalette={colorPalette}
        />,
        KitsOrderedTrendMetadata.name,
      );
    })(),
    loading: loadingShipmentSummary,
  };

  const widgetKitsShippedTotalTrend = {
    ...KitsShippedTotalTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={kitsShippedTotalTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={KitsShippedTotalTrendMetadata.name}
        dataSeries={[{ name: 'Kits Shipped', color: colorPalette?.interpolate(0.32), selector: item => item.kitsShipped }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[KitsShippedTotalTrendMetadata.id]?.chartType?.value || chartTypeEnum.bar}
        colorPalette={colorPalette}
      />,
      KitsShippedTotalTrendMetadata.name,
      KitsShippedTotalTrendMetadata.description,
    ),
    loading: loadingShipmentSummary,
  };

  const widgetTotalKitsShippedTrend = {
    ...TotalKitsShippedTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={totalKitsShippedTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={TotalKitsShippedTrendMetadata.name}
        dataSeries={[
          { name: 'Direct', color: colorPalette?.interpolate(0.35), selector: item => item.directKitsShipped },
          { name: 'Bulk', color: colorPalette?.interpolate(0.55), selector: item => item.bulkKitsShipped },
        ]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[TotalKitsShippedTrendMetadata.id]?.stackedChartType?.value || TotalKitsShippedTrendMetadata.configuration.stackedChartType.defaultValue}
        colorPalette={colorPalette}
      />,
      TotalKitsShippedTrendMetadata.name,
      TotalKitsShippedTrendMetadata.description,
    ),
    loading: loadingShipmentSummary,
  };

  const widgetKitsShippedTrend = {
    ...KitsShippedTrendMetadata,
    noPadding: true,
    render: (() => {
      const dataSeries = shippedPrograms.length > 0
        ? shippedPrograms.map((program, index) => ({
            name: buildReasonLabel(program),
            color: colorPalette?.interpolate((index + 1) / (shippedPrograms.length + 1)),
            selector: item => item[program] || 0,
          }))
        : [{ name: 'Kits Shipped', color: colorPalette?.interpolate(0.4), selector: item => item.total }];
      return graphContainer(
        <GenericCategoryGraph
          graphData={kitsShippedTrendData}
          hasBorder
          forceCondensed
          xAxisTopPosition={false}
          yAxisUnit=""
          tooltipTitle={KitsShippedTrendMetadata.name}
          dataSeries={dataSeries}
          categorySelector={item => item.period}
          hasReferenceAreas
          graphType={configuration?.[KitsShippedTrendMetadata.id]?.stackedChartType?.value || chartTypeEnum.stackedBar}
          colorPalette={colorPalette}
        />,
        KitsShippedTrendMetadata.name,
      );
    })(),
    loading: loadingShipmentSummary,
  };

  const widgetBulkKitsOrderedTrend = {
    ...BulkKitsOrderedTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={bulkKitsOrderedTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={BulkKitsOrderedTrendMetadata.name}
        dataSeries={[{ name: 'Bulk Kits Ordered', color: colorPalette?.interpolate(0.25), selector: item => item.bulkKitsOrdered }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[BulkKitsOrderedTrendMetadata.id]?.chartType?.value || chartTypeEnum.bar}
        colorPalette={colorPalette}
      />,
      BulkKitsOrderedTrendMetadata.name,
    ),
    loading: loadingDeviceUtilizationSummary,
  };

  const widgetBulkKitsShippedTrend = {
    ...BulkKitsShippedTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={bulkKitsShippedTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={BulkKitsShippedTrendMetadata.name}
        dataSeries={[{ name: 'Bulk Kits Shipped', color: colorPalette?.interpolate(0.4), selector: item => item.bulkKitsShipped }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[BulkKitsShippedTrendMetadata.id]?.chartType?.value || chartTypeEnum.bar}
        colorPalette={colorPalette}
      />,
      BulkKitsShippedTrendMetadata.name,
    ),
    loading: loadingDeviceUtilizationSummary,
  };

  const widgetAssignedKitsTrend = {
    ...AssignedKitsTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={assignedKitsTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        tooltipTitle={AssignedKitsTrendMetadata.name}
        dataSeries={[{ name: 'Assigned Kits', color: colorPalette?.interpolate(0.55), selector: item => item.assignedKits }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[AssignedKitsTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      AssignedKitsTrendMetadata.name,
      AssignedKitsTrendMetadata.description,
    ),
    loading: loadingDeviceUtilizationSummary,
  };

  const widgetAssignedDevicesTrend = {
    ...AssignedDevicesTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={assignedDevicesTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        tooltipTitle={AssignedDevicesTrendMetadata.name}
        dataSeries={[{ name: 'Assigned Devices', color: colorPalette?.interpolate(0.75), selector: item => item.assignedDevices }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[AssignedDevicesTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      AssignedDevicesTrendMetadata.name,
      AssignedDevicesTrendMetadata.description,
    ),
    loading: loadingDeviceUtilizationSummary,
  };

  const widgetDevicesShippedTotalTrend = {
    ...DevicesShippedTotalTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={devicesShippedTotalTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={DevicesShippedTotalTrendMetadata.name}
        dataSeries={[{ name: 'Devices Shipped', color: colorPalette?.interpolate(0.68), selector: item => item.devicesShipped }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[DevicesShippedTotalTrendMetadata.id]?.chartType?.value || chartTypeEnum.bar}
        colorPalette={colorPalette}
      />,
      DevicesShippedTotalTrendMetadata.name,
      DevicesShippedTotalTrendMetadata.description,
    ),
    loading: loadingShipmentSummary,
  };

  const widgetTotalDevicesShippedTrend = {
    ...TotalDevicesShippedTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={totalDevicesShippedTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={TotalDevicesShippedTrendMetadata.name}
        dataSeries={[
          { name: 'Direct', color: colorPalette?.interpolate(0.7), selector: item => item.directDevicesShipped },
          { name: 'Bulk', color: colorPalette?.interpolate(0.88), selector: item => item.bulkDevicesShipped },
        ]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[TotalDevicesShippedTrendMetadata.id]?.stackedChartType?.value || TotalDevicesShippedTrendMetadata.configuration.stackedChartType.defaultValue}
        colorPalette={colorPalette}
      />,
      TotalDevicesShippedTrendMetadata.name,
      TotalDevicesShippedTrendMetadata.description,
    ),
    loading: loadingShipmentSummary,
  };

  const widgetKitsShippingQueueTrend = {
    ...KitsShippingQueueTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={kitsShippingQueueTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={KitsShippingQueueTrendMetadata.name}
        dataSeries={[{ name: 'Kits shipping queue', color: colorPalette?.interpolate(0.5), selector: item => item.kitsShippingQueue }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[KitsShippingQueueTrendMetadata.id]?.chartType?.value || KitsShippingQueueTrendMetadata.configuration.chartType.defaultValue}
        colorPalette={colorPalette}
      />,
      KitsShippingQueueTrendMetadata.name,
      KitsShippingQueueTrendMetadata.description,
    ),
    loading: loadingShipmentSummary,
  };


  const widgetDevicesShippedTrend = {
    ...DevicesShippedTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={devicesShippedTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={DevicesShippedTrendMetadata.name}
        dataSeries={[{ name: 'Direct Devices Shipped', color: colorPalette?.interpolate(0.6), selector: item => item.devicesShipped }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[DevicesShippedTrendMetadata.id]?.chartType?.value || chartTypeEnum.bar}
        colorPalette={colorPalette}
      />,
      DevicesShippedTrendMetadata.name,
    ),
    loading: loadingShipmentSummary,
  };

  const widgetBulkDevicesShippedTrend = {
    ...BulkDevicesShippedTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={bulkDevicesShippedTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={BulkDevicesShippedTrendMetadata.name}
        dataSeries={[{ name: 'Bulk Devices Shipped', color: colorPalette?.interpolate(0.75), selector: item => item.bulkDevicesShipped }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[BulkDevicesShippedTrendMetadata.id]?.chartType?.value || chartTypeEnum.bar}
        colorPalette={colorPalette}
      />,
      BulkDevicesShippedTrendMetadata.name,
    ),
    loading: loadingShipmentSummary,
  };

  const widgetAvgOrderToShippedTrend = {
    ...AvgOrderToShippedTrendMetadata,
    noPadding: true,
    render: (() => {
      const averageSeries = [{ name: 'Avg Order to Shipped', color: colorPalette?.interpolate(0.25), selector: item => item.avgOrderToShippedDays }];
      const dataByPeriod = avgOrderToShippedTrendData.reduce((acc, item) => ({ ...acc, [item.period]: item }), {});
      return graphContainer(
        <GenericCategoryGraph
          graphData={avgOrderToShippedTrendData}
          hasBorder
          forceCondensed
          xAxisTopPosition={false}
          yAxisUnit=" d"
          tooltipTitle={AvgOrderToShippedTrendMetadata.name}
          dataSeries={averageSeries}
          labelFormatter={(periodLabel, seriesIndex, value) =>
            formatShipmentAverageTooltip(periodLabel, seriesIndex, value, averageSeries, dataByPeriod, 'orderToShippedCount')}
          categorySelector={item => item.period}
          hasReferenceAreas
          graphType={configuration?.[AvgOrderToShippedTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
          colorPalette={colorPalette}
        />,
        AvgOrderToShippedTrendMetadata.name,
      );
    })(),
    loading: loadingShipmentSummary,
  };

  const widgetBulkAvgOrderToShippedTrend = {
    ...BulkAvgOrderToShippedTrendMetadata,
    noPadding: true,
    render: (() => {
      const averageSeries = [{ name: 'Avg Order to Shipped', color: colorPalette?.interpolate(0.55), selector: item => item.avgOrderToShippedDays }];
      const dataByPeriod = bulkAvgOrderToShippedTrendData.reduce((acc, item) => ({ ...acc, [item.period]: item }), {});
      return graphContainer(
        <GenericCategoryGraph
          graphData={bulkAvgOrderToShippedTrendData}
          hasBorder
          forceCondensed
          xAxisTopPosition={false}
          yAxisUnit=" d"
          tooltipTitle={BulkAvgOrderToShippedTrendMetadata.name}
          dataSeries={averageSeries}
          labelFormatter={(periodLabel, seriesIndex, value) =>
            formatShipmentAverageTooltip(periodLabel, seriesIndex, value, averageSeries, dataByPeriod, 'orderToShippedCount')}
          categorySelector={item => item.period}
          hasReferenceAreas
          graphType={configuration?.[BulkAvgOrderToShippedTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
          colorPalette={colorPalette}
        />,
        BulkAvgOrderToShippedTrendMetadata.name,
      );
    })(),
    loading: loadingShipmentSummary,
  };

  const widgetAvgShippedToActivatedTrend = {
    ...AvgShippedToActivatedTrendMetadata,
    noPadding: true,
    render: (() => {
      const averageSeries = [{ name: 'Avg Shipped to Activated', color: colorPalette?.interpolate(0.45), selector: item => item.avgShippedToActivatedDays }];
      const dataByPeriod = avgShippedToActivatedTrendData.reduce((acc, item) => ({ ...acc, [item.period]: item }), {});
      return graphContainer(
        <GenericCategoryGraph
          graphData={avgShippedToActivatedTrendData}
          hasBorder
          forceCondensed
          xAxisTopPosition={false}
          yAxisUnit=" d"
          tooltipTitle={AvgShippedToActivatedTrendMetadata.name}
          dataSeries={averageSeries}
          labelFormatter={(periodLabel, seriesIndex, value) =>
            formatShipmentAverageTooltip(periodLabel, seriesIndex, value, averageSeries, dataByPeriod, 'shippedToActivatedCount')}
          categorySelector={item => item.period}
          hasReferenceAreas
          graphType={configuration?.[AvgShippedToActivatedTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
          colorPalette={colorPalette}
        />,
        AvgShippedToActivatedTrendMetadata.name,
      );
    })(),
    loading: loadingShipmentSummary,
  };

  const widgetKitUsageTrend = {
    ...KitUsageTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={kitUsageTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit="%"
        tooltipTitle={KitUsageTrendMetadata.name}
        dataSeries={[{ name: 'Avg Kit Usage %', color: colorPalette?.interpolate(0.65), selector: item => item.averageKitUsagePercent }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[KitUsageTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      KitUsageTrendMetadata.name,
      KitUsageTrendMetadata.description,
    ),
    loading: loadingShipmentSummary,
  };

  const widgetDeviceUsageTrend = {
    ...DeviceUsageTrendMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={deviceUsageTrendData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit="%"
        tooltipTitle={DeviceUsageTrendMetadata.name}
        dataSeries={[{ name: 'Avg Device Usage %', color: colorPalette?.interpolate(0.85), selector: item => item.averageDeviceUsagePercent }]}
        categorySelector={item => item.period}
        hasReferenceAreas
        graphType={configuration?.[DeviceUsageTrendMetadata.id]?.chartType?.value || chartTypeEnum.line}
        colorPalette={colorPalette}
      />,
      DeviceUsageTrendMetadata.name,
      DeviceUsageTrendMetadata.description,
    ),
    loading: loadingShipmentSummary,
  };

  const widgetShipmentMethodSplit = {
    ...ShipmentMethodSplitMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={shipmentMethodSplitData}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        yAxisUnit=""
        tooltipTitle={ShipmentMethodSplitMetadata.name}
        dataSeries={[{ name: 'Count', color: colorPalette?.interpolate(0.55), selector: item => item.count }]}
        categorySelector={item => item.shipmentMethod}
        hasReferenceAreas
        graphType={configuration?.[ShipmentMethodSplitMetadata.id]?.chartType?.value || chartTypeEnum.horizontalBar}
        colorPalette={colorPalette}
        yAxisWidth={150}
      />,
      ShipmentMethodSplitMetadata.name,
    ),
    loading: loadingShipmentSummary,
  };

  const organizationWidgets = [
    widgetOrgsState,
    widgetOrgsAdmState,
    widgetOrgBusinessAdmState,
    widgetOrgsOpState,
    widgetNewOrganizationsByType,
    widgetOrganizationsByPatients,
    widgetOrganizationsByEnrollments,
    widgetOrganizationsByKitsOrdered,
    widgetOrganizationsByDevicesOrdered,
  ];

  const enrollmentWidgets = [
    widgetUniquePatientsTrend,
    widgetAvgTimeInProgramTrend,
    widgetEligibleToEnrolledRatioTrend,
    widgetOnboardedToRegisteredTrend,
    widgetRegisteredToEnrolledTrend,
    widgetEnrolledToActivatedTrend,
    ...statusTrendWidgets,
    widgetDisenrollmentReasons,
    widgetNotEnrolledReasons,
    widgetNotEnrolledRecoveries,
  ];
  const overallWidgets = [
    widgetOverallActivePatients,
    widgetOverallAllPatients,
    widgetOverallNewPatientsTrend,
    widgetOverallNewEnrollmentsTrend,
    widgetOverallActiveEnrollmentsTrend,
    widgetOverallPatientsCountTrend,
    widgetOverallDisenrollmentsTrend,
    widgetOverallDisenrollmentReasonsTrend,
    widgetOverallNotEnrolledTrend,
    widgetOverallNotEnrolledReasonsTrend,
  ];

  const shipmentWidgets = [
    widgetKitsOrderedTotalTrend,
    widgetTotalKitsOrderedTrend,
    widgetKitsShippedTotalTrend,
    widgetTotalKitsShippedTrend,
    widgetDevicesShippedTotalTrend,
    widgetTotalDevicesShippedTrend,
    widgetKitsShippingQueueTrend,
    widgetKitsOrderedTrend,
    widgetKitsShippedTrend,
    widgetDevicesShippedTrend,
    widgetAvgOrderToShippedTrend,
    widgetAvgShippedToActivatedTrend,
    widgetBulkKitsOrderedTrend,
    widgetBulkKitsShippedTrend,
    widgetBulkDevicesShippedTrend,
    widgetBulkAvgOrderToShippedTrend,
    widgetShipmentMethodSplit,
  ];

  const deviceVitalActivity = deviceUtilizationSummary.device_vital_activity;
  const deviceVitalActivitySnapshot = deviceVitalActivity
    ? Strings.formatString(Strings.capActivity.snapshot,
      moment(deviceVitalActivity.as_of).tz(deviceVitalActivity.timezone).format('YYYY-MM-DD HH:mm'), deviceVitalActivity.timezone)
    : '';
  // A singleton numeric axis has identical domain limits and renders no bar.
  const widgetCapsLastTake = {
    ...CapsLastTakeMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={deviceVitalActivity?.last_activity_distribution || []}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        xAxisType={deviceVitalActivity?.last_activity_distribution?.length > 1 ? 'number' : 'category'}
        yAxisUnit=""
        tooltipTitle={Strings.capActivity.days}
        dataSeries={[{ name: Strings.capActivity.devices, color: colorPalette?.interpolate(0.6), selector: item => item.devices }]}
        categorySelector={item => item.days_since_last_activity}
        useZeroAsMin
        graphType={configuration?.[CapsLastTakeMetadata.id]?.chartType?.value || CapsLastTakeMetadata.configuration.chartType.defaultValue}
        colorPalette={colorPalette}
      />,
      deviceVitalActivity ? `${CapsLastTakeMetadata.name} · ${Strings.formatString(Strings.capActivity.neverUsed, deviceVitalActivity.never_active_devices)}` : CapsLastTakeMetadata.name,
      `${CapsLastTakeMetadata.description} ${deviceVitalActivitySnapshot}`,
    ),
    loading: loadingDeviceUtilizationSummary,
  };
  const deviceActivity = deviceUtilizationSummary.device_activity;
  const deviceActivitySnapshot = deviceActivity
    ? Strings.formatString(Strings.capActivity.snapshot,
      moment(deviceActivity.as_of).tz(deviceActivity.timezone).format('YYYY-MM-DD HH:mm'), deviceActivity.timezone)
    : '';
  // A singleton numeric axis has identical domain limits and renders no bar.
  const widgetDevicesLastActivity = {
    ...DevicesLastActivityMetadata,
    noPadding: true,
    render: graphContainer(
      <GenericCategoryGraph
        graphData={deviceActivity?.last_activity_distribution || []}
        hasBorder
        forceCondensed
        xAxisTopPosition={false}
        xAxisType={deviceActivity?.last_activity_distribution?.length > 1 ? 'number' : 'category'}
        yAxisUnit=""
        tooltipTitle={Strings.capActivity.activityDays}
        dataSeries={[{ name: Strings.capActivity.devices, color: colorPalette?.interpolate(0.6), selector: item => item.devices }]}
        categorySelector={item => item.days_since_last_activity}
        useZeroAsMin
        graphType={configuration?.[DevicesLastActivityMetadata.id]?.chartType?.value || DevicesLastActivityMetadata.configuration.chartType.defaultValue}
        colorPalette={colorPalette}
      />,
      deviceActivity ? `${DevicesLastActivityMetadata.name} · ${Strings.formatString(Strings.capActivity.noActivity, deviceActivity.never_active_devices)}` : DevicesLastActivityMetadata.name,
      `${DevicesLastActivityMetadata.description} ${deviceActivitySnapshot}`,
    ),
    loading: loadingDeviceUtilizationSummary,
  };
  const deviceUtilizationWidgets = [
    widgetCapsLastTake,
    widgetDevicesLastActivity,
    widgetKitUsageTrend,
    widgetDeviceUsageTrend,
    widgetAssignedKitsTrend,
    widgetAssignedDevicesTrend,
  ];

  setWidgets(
    activeTab === 'overall'
      ? overallWidgets
      : activeTab === 'organizations'
      ? organizationWidgets
      : activeTab === 'device_utilization'
        ? deviceUtilizationWidgets
      : activeTab === 'operational'
        ? shipmentWidgets
        : enrollmentWidgets,
  );
};
